import { Shift, TaskRole, TrainTimetableEntry, Task, RoleGroupName } from '../../common/train';
import { TimelineData, TimelineRow, ShiftTaskInfo, TrainLocation, ShiftInfo } from '../types';
import { PersonTasks } from '../types';
import { _ } from '../../../../shared/utils';
import { format, differenceInHours, differenceInMinutes, getHours, getMinutes, subMinutes, addMinutes } from 'date-fns/esm';
import { TrainDependencyAlarm } from '../../../../alarm/alarm-definitions/train-dependency.alarm';

export function getStationColumn (stationShortCode: string, stationCodes: string[]): number {
  return stationCodes.findIndex(code => code === stationShortCode) * 2 + 2;
}
/**Convert shifts to timeline data */
export function shiftsToTimelineData
  (
    shifts: Shift[],
    roles: TaskRole[],
    trainNumber: string,
    stationCodes: string[],
    shiftConflictAlarms: TrainDependencyAlarm[]
  ): TimelineData[] {
  const personTasks = shifts.reduce((pTasks: PersonTasks[], shift: Shift) => {
    const newTasks = shift.tasks
      .filter(task => task.trainNumber === trainNumber && roles.includes(task.role) && task.fromStation !== task.toStation)
      .reduce((tasks, currTask) => {
        if (tasks.length === 0) {
          return tasks.concat(currTask);
        }
        /**Combine next task to previous if train number and role are same */
        const prevTask = tasks[tasks.length - 1];
        if (prevTask.trainNumber === currTask.trainNumber && prevTask.role === currTask.role) {
          prevTask.toStation = currTask.toStation;
          return tasks;
        } else {
          return tasks.concat(currTask);
        }
      }, [] as Task[]);
    /**Check if same person has multiple shifts */
    const existingTaskIndex = pTasks.findIndex(task => task.personId === shift.personId);
    if (existingTaskIndex !== -1) {
      pTasks[existingTaskIndex].tasks.push(...newTasks);
    }
    return pTasks.concat({personId: shift.personId, tasks: newTasks});
  }, []);

  return personTasks.reduce((timelineData, personTask) => {
    return timelineData.concat(personTask.tasks.map(task => ({
      label: personTask.personId,
      alarm: isShiftConflictByPersonId(personTask.personId, shiftConflictAlarms),
      fromColumn: getStationColumn(task.fromStation, stationCodes),
      toColumn: getStationColumn(task.toStation, stationCodes),
    }))).sort((a, b) => a.fromColumn - b.fromColumn);
    /**Sort by fromStation. Data must be in correct order to show tasks in the same row */
  }, []);
}
/**Convert shifts to timeline chart rows */
export function shiftsToTimelineRows
  (
    trainNumber: string,
    shifts: Shift[],
    stationCodes: string[],
    shiftConflictAlarms: TrainDependencyAlarm[]
  ): TimelineRow[] {
  const conductorShifts = shifts.filter(shift => shift.personGroup === 'CONDUCTOR');
  const driverShifts = shifts.filter(shift => shift.personGroup === 'DRIVER');
  return [
    {
      label: RoleGroupName.Driver,
      datas: shiftsToTimelineData(driverShifts, ['LEAD'], trainNumber, stationCodes, shiftConflictAlarms),
    },
    {
      label: RoleGroupName.AssistDriver,
      datas: shiftsToTimelineData(driverShifts, ['ASSIST'], trainNumber, stationCodes, shiftConflictAlarms),
    },
    {
      label: RoleGroupName.Conductor,
      datas: shiftsToTimelineData(conductorShifts, ['LEAD', 'ASSIST', 'GUIDE', 'TRAINEE'], trainNumber, stationCodes, shiftConflictAlarms),
    },
    {
      label: RoleGroupName.Package,
      datas: shiftsToTimelineData(driverShifts, ['PACKAGE'], trainNumber, stationCodes, shiftConflictAlarms),
    }
  ];
}
/**
 * Get train location
 * Returns train location column index where train currently is (at station or between two stations)
 * */
export function getTrainLocation (trainStopEntries: TrainTimetableEntry[]): TrainLocation {
  const trainLocation: TrainLocation = {columnIndex: undefined, diff: ''};
  const firstStationIndex = 0;
  /** Try to find train location starting from the last station*/
  const reversedStopEntries = trainStopEntries.slice(0).reverse();
  const prevDepartureEntryIndex = reversedStopEntries
    .findIndex(row => row.type === 'DEPARTURE' && row.actual !== null);
  /**Train is at the first station */
  if (prevDepartureEntryIndex === -1) {
    trainLocation.columnIndex = firstStationIndex;
    trainLocation.diff = trainStopEntries[0].diff;
    return trainLocation;
  }
  const nextArrivalEntry = reversedStopEntries[prevDepartureEntryIndex - 1];
  const prevDepartureEntry = reversedStopEntries[prevDepartureEntryIndex];
  if (nextArrivalEntry && nextArrivalEntry.actual) {
    trainLocation.columnIndex = firstStationIndex +
    trainStopEntries.findIndex(entry => entry.actual === nextArrivalEntry.actual) + 1;
    trainLocation.diff = nextArrivalEntry.diff;
  } else {
    trainLocation.columnIndex = firstStationIndex +
    trainStopEntries.findIndex(entry => entry.actual === prevDepartureEntry.actual) + 1;
    trainLocation.diff = prevDepartureEntry.diff;
  }

  return trainLocation;
}
/**
 * Combine tasks with the same taskId
 * @param tasks
 */
export function combineTasks (tasks: Task[]): Task[] {
  const uniqueTaskIds = [...new Set(tasks.map(t => `${t.operatingDate}-${t.taskId}`))];
  const combinedTasks = uniqueTaskIds.map(taskId =>
    tasks
      .filter(t => `${t.operatingDate}-${t.taskId}` === taskId)
      .reduce((task, currTask) => {
        if (task) {
          task.toStation = currTask.toStation;
          task.taskEndDateTime = currTask.taskEndDateTime;
        }
        return task;
      })
  );
  return combinedTasks;
}
function isShiftConflictByTrain(trainNumber: string, shiftConflictAlarms: TrainDependencyAlarm[]) {
  if (!trainNumber || !shiftConflictAlarms.length) {
    return false;
  }
  for (const alarm of shiftConflictAlarms) {
    if (alarm.payload.dependentTripNumber === trainNumber) {
      return true;
    }
  }
  return false;
}
function isShiftConflictByPersonId(personId: string, shiftConflictAlarms: TrainDependencyAlarm[]) {
  if (!personId || !shiftConflictAlarms.length) {
    return false;
  }
  for (const alarm of shiftConflictAlarms) {
    if (alarm.payload.personId === personId) {
      return true;
    }
  }
  return false;
}
export function tasksToShiftTaskInfo(tasks: Task[], shiftConflictAlarms: TrainDependencyAlarm[]): ShiftTaskInfo[] {
  const combinedTasks = combineTasks(tasks);
  const shiftTaskInfos: ShiftTaskInfo[] = combinedTasks.reduce((shiftTaskInfo, task, index) => {
    const nextTask = combinedTasks.length >= index + 1 ? combinedTasks[index + 1] : null;
    const addTransferTask = nextTask ? nextTask.fromStation !== task.toStation : false;
    const taskChangeEndTime = nextTask ? nextTask.taskStartDateTime : null;
    shiftTaskInfo.push(
      {
        taskLabel: task.trainNumber,
        operatingDate: task.operatingDate,
        prevStation: task.fromStation,
        nextStation: task.toStation,
        taskType: 'train',
        taskStartTime: format(new Date(task.taskStartDateTime), 'H:mm'),
        taskEndTime: format(new Date(task.taskEndDateTime), 'H:mm'),
        role: task.role,
        shiftConflict: isShiftConflictByTrain(task.trainNumber, shiftConflictAlarms),
        taskChange: {
          station: task.toStation,
          startTime: format(new Date(task.taskEndDateTime), 'H:mm'),
          endTime: addTransferTask ? '' : format(new Date(taskChangeEndTime), 'H:mm')
        }
      });
    /**Person moves(by walking or taxi etc.) to next station if task's toStation is different than next task's fromStation.
     * We need to add a new transfer task to show transfer on the personnel rotation tooltip
     */
    if (addTransferTask) {
      shiftTaskInfo.push(
        {
          taskLabel: _('Transfer'),
          taskType: 'transfer',
          role: '',
          taskChange: {
            station: nextTask.fromStation,
            startTime: '',
            endTime: format(new Date(taskChangeEndTime), 'H:mm')
          },
        });
    }
    return shiftTaskInfo;
  }, []);
  return shiftTaskInfos;
}

function formatTime(time: number) {
  if (time == null) {
    return '-';
  } else if (time < 10) {
    return `0${time}`;
  }
  return `${time}`;
}

function getActualShiftStartTime(shift: Shift) {
  const firstTaskStart = shift.tasks.length ? shift.tasks[0].taskStartDateTime : null;
  let actualStartTime = '-:-';
  let actualStartDate: Date;
  if (firstTaskStart && shift.tasks[0].preparationMinutes != null) {
    actualStartDate = subMinutes(firstTaskStart, shift.tasks[0].preparationMinutes);
    actualStartTime = `${formatTime(getHours(actualStartDate))}:${formatTime(getMinutes(actualStartDate))}`;
  } else if (firstTaskStart) {
    actualStartTime = `${formatTime(getHours(firstTaskStart))}:${formatTime(getMinutes(firstTaskStart))}`;
  }
  return actualStartTime;
}

function getActualShiftEndTime(shift: Shift) {
  const lastTaskEnd = shift.tasks.length ? shift.tasks[shift.tasks.length - 1].taskEndDateTime : null;
  let actualEndTime = '-:-';
  let actualEndDate: Date;
  if (lastTaskEnd && shift.tasks[shift.tasks.length - 1].wrapUpMinutes != null) {
    actualEndDate = addMinutes(lastTaskEnd, shift.tasks[shift.tasks.length - 1].wrapUpMinutes);
    actualEndTime = `${formatTime(getHours(actualEndDate))}:${formatTime(getMinutes(actualEndDate))}`;
  } else if (lastTaskEnd) {
    actualEndTime = `${formatTime(getHours(lastTaskEnd))}:${formatTime(getMinutes(lastTaskEnd))}`;
  }
  return actualEndTime;
}

function getActualShiftLength(shift) {
  let shiftLength = '-:-';
  let actualStartDate: Date;
  let actualEndDate: Date;
  const firstTaskStartDateTime = shift.tasks.length ? shift.tasks[0].taskStartDateTime : null;
  const lastTaskEndDateTime = shift.tasks.length ? shift.tasks[shift.tasks.length - 1].taskEndDateTime : null;

  if (firstTaskStartDateTime && lastTaskEndDateTime) {
    actualStartDate = subMinutes(firstTaskStartDateTime, shift.tasks[0].preparationMinutes != null ? shift.tasks[0].preparationMinutes : 0);
    actualEndDate = addMinutes(
      lastTaskEndDateTime,
      shift.tasks[shift.tasks.length - 1].wrapUpMinutes != null ? shift.tasks[shift.tasks.length - 1].wrapUpMinutes : 0
    );
    shiftLength =
      `${formatTime(differenceInHours(actualEndDate, actualStartDate))}:${formatTime(differenceInMinutes(actualEndDate, actualStartDate) % 60)}`;
  }
  return shiftLength;
}

export function shiftToShiftInfo(shift: Shift): ShiftInfo {
  const shiftLength = getActualShiftLength(shift);
  const startTime = getActualShiftStartTime(shift);
  const endTime = getActualShiftEndTime(shift);

  return {shiftId: shift.shiftId, personId: shift.personId, shiftLength, startTime, endTime};
}
